import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AluminumPlate, { ALUMINUM_SIZES } from "@/components/AluminumPlate";
import { Printer, IdentificationCard, PaperPlaneTilt } from "@phosphor-icons/react";

const CATEGORIES = [
    { code: "private",   labelKey: "aluminumPrivate" },
    { code: "taxi",      labelKey: "aluminumTaxi" },
    { code: "temporary", labelKey: "aluminumTemporary" },
    { code: "electric",  labelKey: "aluminumElectric" },
    { code: "motorcycle",labelKey: "aluminumMotorcycle" },
];

const SIZE_META = {
    long:  { labelKey: "aluminumLong",  categories: ["private", "taxi", "temporary", "electric"] },
    short: { labelKey: "aluminumShort", categories: ["private", "taxi", "temporary", "electric"] },
    moto:  { labelKey: "aluminumMoto",  categories: ["motorcycle", "electric"] },
};

export default function AluminumDesigner() {
    const { t } = useI18n();
    const [plates, setPlates] = useState([]);
    const [selectedPlateId, setSelectedPlateId] = useState("");
    const [lpn, setLpn] = useState("B-UT 2026");
    const [size, setSize] = useState("long");
    const [category, setCategory] = useState("private");
    const [expiry, setExpiry] = useState("12/26");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/plates").then((r) => {
            const list = (r.data.plates || []).slice(0, 200);
            setPlates(list);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!SIZE_META[size].categories.includes(category)) {
            setCategory(SIZE_META[size].categories[0]);
        }
    }, [size]); // eslint-disable-line

    useEffect(() => {
        if (!selectedPlateId) return;
        const p = plates.find((x) => x.id === selectedPlateId);
        if (p) {
            setLpn(p.plate_number || lpn);
            const e = (p.expires_at || "").slice(0, 10);
            if (e) setExpiry(`${e.slice(5, 7)}/${e.slice(2, 4)}`);
            if (p.plate_category) setCategory(p.plate_category);
        }
    }, [selectedPlateId]); // eslint-disable-line

    // Sensible sample LPNs per size when no plate is picked.
    useEffect(() => {
        if (selectedPlateId) return;
        const samples = { long: "B-UT 2026", short: "B-UT 471", moto: "B-U 47" };
        setLpn(samples[size] || "B-UT 2026");
    }, [size, selectedPlateId]);

    const sendToProduction = async () => {
        if (!selectedPlateId) {
            toast.error(t("aluminumPickPlateFirst"));
            return;
        }
        setBusy(true);
        try {
            const preset = size === "long" ? "de-long" : size === "short" ? "de-short" : "de-moto";
            const { data } = await api.post(`/plates/${selectedPlateId}/aluminum-zpl`, {
                preset, category,
            });
            // Also persist the category on the plate for downstream lookups.
            await api.patch(`/plates/${selectedPlateId}/category`, { plate_category: category });
            const win = window.open("", "_blank");
            if (win) {
                win.document.write(`<pre style="font:12px monospace;padding:20px">${data.zpl}</pre>`);
                win.document.title = `ZPL — ${lpn}`;
            }
            toast.success(t("aluminumSentToProduction"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error"));
        }
        setBusy(false);
    };

    return (
        <div className="max-w-5xl mx-auto space-y-6" data-testid="aluminum-designer-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· DE-STANDARD</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("aluminumDesignerTitle")}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t("aluminumDesignerHint")}</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Card className="p-5 rounded-sm border-border space-y-4">
                    <div>
                        <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("stickerNumber")}</Label>
                        <select
                            value={selectedPlateId}
                            onChange={(e) => setSelectedPlateId(e.target.value)}
                            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mt-1"
                            data-testid="alu-plate-select"
                        >
                            <option value="">— {t("aluminumPickPlate")} —</option>
                            {plates.map((p) => (
                                <option key={p.id} value={p.id}>{p.plate_number} · {p.owner_name || "—"}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("plateNumber")} (LPN)</Label>
                        <Input value={lpn} onChange={(e) => setLpn(e.target.value.toUpperCase())} className="mono h-12 text-lg mono mt-1" data-testid="alu-lpn" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("aluminumSize")}</Label>
                            <select value={size} onChange={(e) => setSize(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mt-1" data-testid="alu-size">
                                {Object.entries(SIZE_META).map(([k, m]) => {
                                    const dims = ALUMINUM_SIZES[k];
                                    return <option key={k} value={k}>{t(m.labelKey)} ({dims.w}×{dims.h})</option>;
                                })}
                            </select>
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("aluminumCategory")}</Label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mt-1" data-testid="alu-category">
                                {CATEGORIES.filter((c) => SIZE_META[size].categories.includes(c.code)).map((c) => (
                                    <option key={c.code} value={c.code}>{t(c.labelKey)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {category === "temporary" && (
                        <div>
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("aluminumExpiryBand")} (MM/YY)</Label>
                            <Input value={expiry} onChange={(e) => setExpiry(e.target.value)} placeholder="12/26" className="mono mt-1" data-testid="alu-expiry" />
                        </div>
                    )}
                    <Button
                        onClick={sendToProduction}
                        disabled={busy || !selectedPlateId}
                        className="w-full h-12 rounded-sm bg-primary hover:bg-primary/90"
                        data-testid="alu-send-production"
                    >
                        <PaperPlaneTilt size={18} weight="bold" className="me-1" />
                        {busy ? t("loading") : t("aluminumSendToProduction")}
                    </Button>
                    {!selectedPlateId && <div className="text-[11px] text-muted-foreground">{t("aluminumPickPlateFirst")}</div>}
                </Card>

                <Card className="p-5 rounded-sm border-border flex items-center justify-center bg-secondary/30 min-h-[280px]" data-testid="alu-preview-wrap">
                    <div className="w-full max-w-full flex items-center justify-center" style={{ padding: 12 }}>
                        <AluminumPlate plateNumber={lpn} size={size} category={category} expiryMmYy={expiry} scale="fit" />
                    </div>
                </Card>
            </div>
        </div>
    );
}
